/**
 * Usage reporting contract.
 *
 * Forge reports usage from two kinds of source, and both answer in the same
 * bucket shape so the client can merge them:
 *
 * 1. **Transcripts** — each connected environment scans the provider CLIs' own
 *    on-disk session files (`~/.claude/projects/**\/*.jsonl`,
 *    `~/.codex/sessions/**\/*.jsonl`) rather than Forge's orchestration
 *    projections, so usage stays complete even for turns that were never driven
 *    through Forge. This mirrors the approach `ccusage` takes.
 * 2. **CMMD** — CMMD's usage ledger (`forge_turn_usage`), one row per Recorded
 *    turn every environment replicates. Reaches work whose machine is not
 *    currently connected, and is canonical for any turn it holds.
 * 3. **Recorded** — an environment's own Recorded turns read from its
 *    projection, with a sync marker saying whether CMMD has acknowledged them.
 *    Pending turns are what the page shows before the ledger catches up.
 *
 * Sources return pre-aggregated `(day, hourStart?, provider, model)` buckets.
 * Raw transcript records never cross the wire.
 *
 * @module usage
 */
import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas";

/**
 * Bumped whenever the shape of {@link UsageSummary} changes incompatibly. The
 * client renders partial coverage when a source reports an older version rather
 * than failing the whole page.
 *
 * Still 2 after the kind keys: `UsageBucket.kind` and `UsageSummary.kinds` are
 * both optional, a summary without either is totalled exactly as before, and a
 * bump would report every source that has not shipped them as stale.
 */
export const USAGE_CONTRACT_VERSION = 2 as const;

/**
 * Oldest contract version the merge still accepts. A mixed fleet is normal: a
 * sprite on the previous image, CMMD on its own release cadence, this client
 * on today's. Version 2 added nothing a version 1 source lacks that the merge
 * needs, so a version 1 summary is merged rather than reported as stale.
 * Raise this only when an older shape can no longer be totalled honestly.
 */
export const USAGE_MERGE_COMPATIBLE_SINCE = 1 as const;

/**
 * The harness a bucket's tokens were spent through.
 *
 * `cmmd` is the CMMD platform itself (Sidekick, CMMD web surfaces). Provider
 * calls that CMMD merely brokered on a Forge runtime's behalf are reported
 * under the harness that actually ran them — `claude` or `codex` — so a lane
 * means the same thing regardless of which source observed it.
 */
export const UsageProviderKind = Schema.Literals(["claude", "codex", "cmmd"]);
export type UsageProviderKind = typeof UsageProviderKind.Type;

/**
 * A calendar day in the reporting time zone, formatted `YYYY-MM-DD`.
 *
 * Days are bucketed at the source so a turn always lands on the day the user
 * experienced it, not the UTC day.
 */
const USAGE_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const UsageDay = TrimmedNonEmptyString.check(Schema.isPattern(USAGE_DAY_PATTERN)).pipe(
  Schema.brand("UsageDay"),
);
export type UsageDay = typeof UsageDay.Type;

export const UsageResolution = Schema.Literals(["day", "hour"]);
export type UsageResolution = typeof UsageResolution.Type;

/**
 * Why a bucket's cost is what it is.
 *
 * - `providerReported` — the transcript or the CMMD ledger carried an explicit
 *   cost figure.
 * - `modelPriced` — we matched the model against the LiteLLM rate table.
 * - `unpriced` — tokens are known, rates are not. Counted in token totals,
 *   excluded from cost.
 */
export const UsageCostSource = Schema.Literals(["providerReported", "modelPriced", "unpriced"]);
export type UsageCostSource = typeof UsageCostSource.Type;

/**
 * Token counts for a bucket.
 *
 * `cachedInputTokens` and `cacheCreationTokens` are disjoint from
 * `uncachedInputTokens`; summing all three gives total input. `reasoningTokens`
 * is a *subset* of `outputTokens` (Codex reports it that way, and Anthropic
 * folds thinking into output), so it must never be added on top.
 */
export const UsageTokenTotals = Schema.Struct({
  uncachedInputTokens: NonNegativeInt,
  cachedInputTokens: NonNegativeInt,
  cacheCreationTokens: NonNegativeInt,
  outputTokens: NonNegativeInt,
  reasoningTokens: NonNegativeInt,
});
export type UsageTokenTotals = typeof UsageTokenTotals.Type;

/**
 * One `(day, hourStart?, provider, model)` cell. `hourStart` is the UTC start
 * instant of a rolling bucket and is present only for hourly requests.
 *
 * `costUsd` is the raw API-equivalent cost of these tokens. It is not money
 * spent: subscription plans bill separately. `unpricedRecords` counts records
 * whose tokens are included in the token totals but which contributed nothing
 * to `costUsd`.
 */
export const UsageBucketOrigin = Schema.Literals(["recorded", "transcript"]);
export type UsageBucketOrigin = typeof UsageBucketOrigin.Type;

export const UsageBucketSync = Schema.Literals(["acknowledged", "pending"]);
export type UsageBucketSync = typeof UsageBucketSync.Type;

/**
 * The harness that drove a transcript session, read from what the session
 * file says about itself.
 *
 * - `forge` — agent work under a thread this environment ran that no Recorded
 *   turn carries: a Claude subagent's turns inside one of its sessions, a
 *   Codex subagent rollout whose root thread is one of its threads. Shown
 *   apart from the ledger, under this environment's row.
 * - `forge_unrecorded` — a Codex rollout stamped `originator: forge_desktop`
 *   that this environment did not run: another Forge server on the same
 *   machine, or a thread whose session row was deleted. Forge's work, but not
 *   this server's to attribute to a runtime, so it stays Local-only.
 * - `t3code` — the t3code desktop app (`originator: t3code_desktop`).
 * - `codex_desktop` / `claude_desktop` — the vendors' own desktop apps.
 * - `cli` — a person at the terminal or an editor (`entrypoint: cli`,
 *   `codex exec`, the Codex TUI).
 * - `sdk` — an Agent SDK host this build cannot name (`entrypoint: sdk-ts`,
 *   `sdk-py`, `sdk-cli`).
 * - `other` — a launcher string this build does not know. Kept visible rather
 *   than folded into a known bucket, so a renamed harness shows up as a gap.
 */
export const UsageDriver = Schema.Literals([
  "forge",
  "forge_unrecorded",
  "t3code",
  "codex_desktop",
  "claude_desktop",
  "cli",
  "sdk",
  "other",
]);
export type UsageDriver = typeof UsageDriver.Type;

/**
 * What a Usage row is a row of.
 *
 * - `turn` - a Recorded turn's own work. Rows that carry no kind read as this,
 *   which is what every row recorded before the ledger learned about kinds is.
 * - `subagent` - work a subagent did inside a Recorded turn. It keeps the
 *   parent's turn id, so the turn still counts once.
 * - `text_generation` - a one-shot call Forge made on its own behalf for a
 *   thread title, a commit message, a branch name, or pull request text. Never
 *   a Recorded turn.
 *
 * `CONTEXT.md` section Usage defines all three; `docs/adr/usage-rows-have-a-kind.md`
 * records why Claude subagent work is folded into the parent's rows instead of
 * being split out.
 */
export const UsageKind = Schema.Literals(["turn", "subagent", "text_generation"]);
export type UsageKind = typeof UsageKind.Type;

export const UsageBucket = Schema.Struct({
  day: UsageDay,
  hourStart: Schema.optional(TrimmedNonEmptyString),
  provider: UsageProviderKind,
  model: TrimmedNonEmptyString,
  totals: UsageTokenTotals,
  costUsd: Schema.Number,
  /**
   * What the cached input would have cost at full input rates minus what it
   * actually cost. Requires the rate table, so it is computed alongside cost
   * rather than derived on the client.
   */
  cacheSavingsUsd: Schema.Number,
  costSource: UsageCostSource,
  /** Distinct assistant responses, after de-duplication. */
  records: NonNegativeInt,
  unpricedRecords: NonNegativeInt,
  /**
   * Records that carry no tokens at all, so nothing could be priced.
   *
   * Unreported means the turn ran before the publisher existed and carries no
   * tokens; unpriced means the tokens are known and the rate is not. CMMD
   * sends this once CMMD-36487 deploys, and a server that predates it omits
   * the key, so a missing value reads as unknown rather than as zero.
   */
  unreportedRecords: Schema.optional(NonNegativeInt),
  /** Distinct transcript sessions that contributed to this cell. */
  sessions: NonNegativeInt,
  /**
   * The Forge runtime whose work produced this cell, when the source knows it.
   *
   * CMMD's ledger rows and an environment's own Recorded turns carry it.
   * Absent for buckets that belong to no runtime (Sidekick, the CMMD web app,
   * a CLI session run outside Forge); those are always counted.
   */
  runtimeId: Schema.optional(TrimmedNonEmptyString),
  /**
   * Where a local environment's bucket came from. `recorded` is the
   * projection's own Recorded turns; `transcript` is the provider CLI's
   * session files. Absent on CMMD buckets and on summaries from builds that
   * predate the key. Added on contract version 2 as an optional key.
   */
  origin: Schema.optional(UsageBucketOrigin),
  /**
   * Whether CMMD has acknowledged the rows in this cell.
   *
   * Present on `recorded` buckets from an environment with a replication lane,
   * and on a `subagent` bucket once the environment that scanned it has
   * replicated those rows. Absent means the environment has no lane, or has
   * nothing to say about this cell, so CMMD never saw these rows and the merge
   * must keep them. The merge drops `acknowledged` buckets when CMMD answered,
   * because CMMD's ledger row for the same work is the canonical one.
   */
  sync: Schema.optional(UsageBucketSync),
  /**
   * Which harness drove the sessions in a `transcript` cell.
   *
   * Present only on transcript buckets from builds that classify sessions.
   * `forge` is agent work this environment drove that the ledger does not
   * hold; the merge totals it on its own lane and never folds it into CMMD's
   * figures. Absent on Recorded and CMMD buckets. Optional on contract
   * version 2.
   */
  driver: Schema.optional(UsageDriver),
  /**
   * What the rows in this cell are rows of.
   *
   * Absent on every summary from a build that predates the key, and on rows
   * recorded before the ledger learned about kinds, so a bucket without one
   * reads as `turn`. A current server stamps it on every bucket it produces:
   * a Recorded turn from the replication serializer's own `usageKind`, a
   * transcript record from the on-disk fact (Claude's sidechain flag, a Codex
   * subagent rollout) that also names the forge-agents lane. For a summary
   * from an older build whose scanner left the key unstamped, the merge falls
   * back to inferring `subagent` for a transcript bucket attributed to a
   * Forge-driven agent, since that lane is subagent work by definition.
   */
  kind: Schema.optional(UsageKind),
});
export type UsageBucket = typeof UsageBucket.Type;

/**
 * A source's totals for one kind, across the whole window.
 *
 * Reported beside the buckets rather than derived from them, because `turns`
 * cannot be: a bucket is a `(day, provider, model)` cell and a turn that spans
 * two models has a row in two of them. Only a source that holds turn ids can
 * count distinct turns, which today is CMMD's ledger. Sums to the same money
 * and tokens as the source's buckets, so the merge reads one or the other and
 * never both.
 */
export const UsageKindTotals = Schema.Struct({
  kind: UsageKind,
  /** Usage rows of this kind. A turn spanning two models has two. */
  records: NonNegativeInt,
  /** Distinct turn ids among those rows. One per turn, however many rows it has. */
  turns: NonNegativeInt,
  totals: UsageTokenTotals,
  costUsd: Schema.Number,
  cacheSavingsUsd: Schema.Number,
});
export type UsageKindTotals = typeof UsageKindTotals.Type;

/**
 * Which reporting mechanism produced a source.
 *
 * `recorded` is the projection's own Recorded turns, reported by the
 * environment that ran them. Added on contract version 2 as a new literal;
 * sources of that kind only appear in summaries from builds that emit it.
 */
export const UsageSourceKind = Schema.Literals(["transcripts", "cmmd", "recorded"]);
export type UsageSourceKind = typeof UsageSourceKind.Type;

/**
 * Identifies the physical transcript directory a source read from.
 *
 * Two environments on the same machine (worktree servers, for example) resolve
 * the same provider home and would otherwise double count. The client drops
 * duplicate fingerprints before merging.
 */
export const UsageSourceFingerprint = Schema.Struct({
  kind: UsageSourceKind,
  hostId: TrimmedNonEmptyString,
  provider: UsageProviderKind,
  resolvedHomePath: TrimmedNonEmptyString,
  /**
   * Filesystem identity of the transcript directory, as `device:inode`.
   *
   * Hostname and path alone are not enough: every Mac in a fleet resolves
   * `/Users/<user>/.claude`, so two machines that happen to share a hostname
   * would look like one source and have their usage silently dropped. The
   * device/inode pair is stable for two servers reading the same directory and
   * effectively never collides across machines. Empty when it cannot be read.
   */
  volumeId: Schema.String,
});
export type UsageSourceFingerprint = typeof UsageSourceFingerprint.Type;

export const UsageSourceStatus = Schema.Literals(["ok", "missing", "partial", "failed"]);
export type UsageSourceStatus = typeof UsageSourceStatus.Type;

export const UsageSource = Schema.Struct({
  fingerprint: UsageSourceFingerprint,
  status: UsageSourceStatus,
  scannedFiles: NonNegativeInt,
  skippedFiles: NonNegativeInt,
  /**
   * Lines that failed to parse as JSON among those the scan opened. A
   * truncated tail on a live transcript is the usual cause; a count that keeps
   * growing means a file the scan can no longer read.
   */
  malformedRecords: NonNegativeInt,
  /**
   * Distinct transcript sessions seen under this directory. Buckets also carry
   * per-bucket session counts, but a session spans days and models, so summing
   * those overcounts; this is the figure clients should total.
   */
  distinctSessions: NonNegativeInt,
  /**
   * Forge runtimes this source speaks for.
   *
   * A `recorded` source names the runtime whose projection it read, and a
   * transcript source names the runtime it runs on. This is how the merge
   * decides which source speaks for a runtime, so a machine's Local-only lane,
   * its Recorded turns and CMMD's ledger rows for it all land on one row.
   * CMMD is canonical for a turn it holds, so the merge drops the runtime's own
   * acknowledged Recorded buckets rather than CMMD's rows. CMMD's own
   * fingerprint names the runtimes it holds rows for and speaks for none of
   * them, so the merge ignores its ids when it decides that.
   */
  runtimeIds: Schema.Array(TrimmedNonEmptyString),
  message: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * Transcript sessions this scan skipped because a Forge thread on this
   * environment ran them (matched by the provider's resume cursor). Their
   * usage is reported as Recorded turns instead. Present only on
   * `transcripts` sources from builds that split the lanes.
   */
  excludedForgeSessions: Schema.optional(NonNegativeInt),
  /**
   * Transcript sessions this scan attributed to Forge-driven agents that no
   * Recorded turn carries: Claude subagent turns inside a Forge session and
   * Codex subagent rollouts under a Forge thread. Their buckets carry
   * `driver: "forge"`. Not part of `distinctSessions`, because the Recorded
   * lane already counts the thread they ran under.
   */
  forgeAgentSessions: Schema.optional(NonNegativeInt),
  /**
   * Forge-shaped sessions in this directory that the Usage ledger already
   * holds under another Environment. They leave the Local-only and Forge
   * agents lanes, because the ledger row is the record. A session held under
   * the Environment that scanned it is not counted here; it was never
   * anywhere else.
   */
  recordedElsewhereSessions: Schema.optional(NonNegativeInt),
  /**
   * Forge-shaped sessions this scan could not ask the ledger about because
   * the call failed. Their usage stays in the local lanes: a control plane
   * that is down must never blank the page. The count says the suppression is
   * incomplete, not that the numbers are wrong.
   */
  lookupUnavailableSessions: Schema.optional(NonNegativeInt),
  /**
   * Forge-shaped sessions this scan never asked about because the environment
   * holds no runtime grant, which is every desktop. Distinct from
   * `lookupUnavailableSessions`: nothing failed, the lane does not exist here
   * yet.
   */
  lookupUnsupportedSessions: Schema.optional(NonNegativeInt),
});
export type UsageSource = typeof UsageSource.Type;

export const UsagePricingStatus = Schema.Literals(["fresh", "cached", "unavailable"]);
export type UsagePricingStatus = typeof UsagePricingStatus.Type;

/**
 * Provenance for the rate table, so the UI can be honest about how good the
 * cost figures are.
 */
export const UsagePricing = Schema.Struct({
  status: UsagePricingStatus,
  source: TrimmedNonEmptyString,
  fetchedAt: Schema.NullOr(Schema.String),
  knownModels: NonNegativeInt,
});
export type UsagePricing = typeof UsagePricing.Type;

export const UsageSummaryInput = Schema.Struct({
  /** Inclusive first day of the window, in `timeZone`. */
  sinceDay: UsageDay,
  /** Inclusive last day of the window, in `timeZone`. */
  untilDay: UsageDay,
  /**
   * IANA zone the client wants days bucketed in. An offset would be wrong for
   * any window that crosses a DST boundary.
   */
  timeZone: TrimmedNonEmptyString,
  /** Defaults to daily for older clients. */
  resolution: Schema.optional(UsageResolution),
  /** Inclusive UTC instant for an hourly rolling window. */
  sinceTime: Schema.optional(TrimmedNonEmptyString),
  /** Exclusive UTC instant for an hourly rolling window. */
  untilTime: Schema.optional(TrimmedNonEmptyString),
});
export type UsageSummaryInput = typeof UsageSummaryInput.Type;

export const UsageSummary = Schema.Struct({
  contractVersion: Schema.Number,
  readAt: Schema.String,
  timeZone: TrimmedNonEmptyString,
  sinceDay: UsageDay,
  untilDay: UsageDay,
  buckets: Schema.Array(UsageBucket),
  /**
   * Per-kind totals over the same rows the buckets hold, when the source can
   * count distinct turns. CMMD's ledger sends it; a transcript scan cannot,
   * because a session file carries no turn id, so its buckets carry the kind
   * instead. Absent on summaries from builds that predate the key.
   */
  kinds: Schema.optional(Schema.Array(UsageKindTotals)),
  sources: Schema.Array(UsageSource),
  pricing: UsagePricing,
  /** Wall-clock cost of the scan, surfaced in diagnostics. */
  scanDurationMs: NonNegativeInt,
});
export type UsageSummary = typeof UsageSummary.Type;

export class UsageReadError extends Schema.TaggedErrorClass<UsageReadError>()("UsageReadError", {
  reason: Schema.Literals(["scanFailed", "invalidWindow"]),
  /** Stable, bounded description. The underlying failure travels in `cause`. */
  detail: TrimmedNonEmptyString,
  cause: Schema.optional(Schema.Defect),
}) {
  // fallow-ignore-next-line unused-class-member
  override get message(): string {
    return `Usage read failed (${this.reason}): ${this.detail}`;
  }
}
